import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
/** How long a URL must have been shown before a failure reads as its expiry. */
export const STALE_LOGO_URL_MS = 5 * 60 * 1000;

export interface RecordLogoProps {
  /** The logo to show, a resolved URL; null when there is none to show. */
  imageUrl: string | null;
  /** Shown when `imageUrl` is null or fails to load. A string is an image URL. */
  fallback: string | ReactNode;
  /** What the logo is, lower case: "event logo", "vendor logo". */
  label: string;
  /** Whether the record has a logo of its own, so "Remove logo" is offered. */
  hasOwnLogo: boolean;
  /** Show the hover edit overlay and the remove button. */
  editable?: boolean;
  /** Uploads the picked file and attaches it to the record. */
  upload: (file: File) => Promise<unknown>;
  /** Clears the record's logo. */
  clear: () => Promise<unknown>;
  /** Called once an upload or a clear has saved. */
  onChanged?: () => void;
  /**
   * Called when an image that has been on screen a while fails to load. A
   * presigned URL expires within the hour, and a page left open that long would
   * otherwise show the fallback until something else refetched the record. A
   * URL that fails as soon as it arrives is not stale — every refetch signs a
   * new one, so asking again would go round for ever over a missing file.
   */
  onExpired?: () => void;
  /** Tailwind size class — defaults to `w-12 h-12`. */
  sizeClassName?: string;
  inputId?: string;
  /** The file input's test id. */
  inputTestId?: string;
}

/**
 * A record's logo — an event's, a vendor's. Shows the image, else the
 * fallback; when `editable`, hovering offers a pencil that opens a file picker,
 * and the record's own logo can be removed. What uploading and clearing mean
 * is the caller's (`upload`, `clear`), so every record type reuses this.
 */
export function RecordLogo({
  imageUrl,
  fallback,
  label,
  hasOwnLogo,
  editable = false,
  upload: uploadFile,
  clear: clearLogo,
  onChanged,
  onExpired,
  sizeClassName = 'w-12 h-12',
  inputId,
  inputTestId,
}: RecordLogoProps) {
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shownSince = useRef(Date.now());
  const fallbackSrc = typeof fallback === 'string' ? fallback : null;
  const title = label.charAt(0).toUpperCase() + label.slice(1);

  // A new URL gets its own chance to load, and its own clock.
  useEffect(() => {
    setFailed(false);
    shownSince.current = Date.now();
  }, [imageUrl]);

  const upload = useMutation({
    mutationFn: uploadFile,
    onSuccess: () => onChanged?.(),
    onError: () => setError('Failed to upload logo. Try a different image.'),
  });
  const clear = useMutation({
    mutationFn: clearLogo,
    onSuccess: () => onChanged?.(),
    onError: () => setError('Failed to remove the logo. Try again.'),
  });

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Use a JPG, PNG, WebP or SVG.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Logo must be smaller than 5 MB.');
      return;
    }
    setError(null);
    upload.mutate(file);
  }

  const isLoading = upload.isPending || clear.isPending;
  const src = imageUrl && !failed ? imageUrl : fallbackSrc;

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <div className={`relative ${sizeClassName} flex-shrink-0`}>
        {src ? (
          <img
            src={src}
            onError={() => {
              setFailed(true);
              if (imageUrl && Date.now() - shownSince.current >= STALE_LOGO_URL_MS) onExpired?.();
            }}
            alt={title}
            // No frame and no crop: a transparent logo stays transparent, and a
            // wide one is fitted rather than having its sides cut off.
            className={`${sizeClassName} rounded-lg object-contain`}
          />
        ) : (
          <div
            role="img"
            aria-label={title}
            className={`${sizeClassName} flex items-center justify-center rounded-lg bg-primary-50 text-primary-600`}
          >
            {fallback}
          </div>
        )}
        {editable && (
          <>
            <button
              type="button"
              onClick={() => {
                setError(null);
                fileInputRef.current?.click();
              }}
              disabled={isLoading}
              aria-label={`Change ${label}`}
              className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 hover:bg-black/40 transition-colors group"
            >
              {isLoading ? (
                <Loader2 size={18} className="text-white animate-spin" />
              ) : (
                <Pencil
                  size={16}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                />
              )}
            </button>
            <input
              ref={fileInputRef}
              id={inputId}
              type="file"
              accept={ALLOWED_TYPES.join(',')}
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                // Picking the same file again after a failure still uploads.
                e.target.value = '';
              }}
              className="hidden"
              data-testid={inputTestId}
            />
          </>
        )}
      </div>
      {editable && hasOwnLogo && !isLoading && (
        <button
          type="button"
          onClick={() => clear.mutate()}
          className="text-xs text-gray-500 hover:text-red-600"
        >
          Remove logo
        </button>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
